import { AlertCircle, BarChart3, Briefcase, Building2, UserMinus, Users } from 'lucide-react'
import type {PlanChangePreflightResponse, PlanCode} from '@/lib/types/subscription';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  PLAN_CHANGE_BLOCKED_CODES
  
  
} from '@/lib/types/subscription'
import { getPlanDisplayName } from '@/lib/types/subscriptionHelpers'

interface PlanChangeBlockedModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * The plan the user attempted to switch to. Used for the modal title and
   * the per-violation copy ("Individual requires …").
   */
  targetPlan: PlanCode | null
  /**
   * Preflight payload from /subscriptions/plan-change-preflight. The same
   * modal renders Phase 0 violations (members / workspace_type / guests) and
   * Phase 3 violations (quota / workspaces) — payload is forward-compatible.
   */
  preflight: PlanChangePreflightResponse | null
  /**
   * Optional click handlers per violation type so the consumer can deep-link
   * the user to the right cleanup screen (member list, workspace settings,
   * etc.). When omitted, the modal just renders the message without action.
   */
  onResolveMembers?: () => void
  onResolveWorkspaceType?: () => void
  onResolveGuests?: () => void
  /** Phase 3: deep-link to projects/tasks list to clean up over-limit items. */
  onResolveQuota?: (limitKey: string) => void
  /** Phase 3: deep-link to workspaces list when the user owns another Free workspace. */
  onResolveWorkspaces?: () => void
}

interface ViolationDescriptor {
  icon: typeof AlertCircle
  title: string
  description: string
  cta?: string
  onResolve?: () => void
}

function friendlyQuotaKey(key: string | undefined): string {
  switch (key) {
    case 'members':
      return 'members'
    case 'tasks':
      return 'tasks'
    case 'projects':
      return 'projects'
    case 'storage':
      return 'storage'
    case 'guests':
      return 'guests'
    default:
      return key ?? 'usage'
  }
}

/**
 * Phase 0 PlanChangeBlockedModal. Surfaces the structured plan_change_blocked_*
 * violations that come back from CreateCheckoutSession / ChangePlan / the
 * dedicated plan-change-preflight endpoint, with a fix CTA per violation
 * (remove members, switch workspace type, remove guests).
 *
 * Same modal serves both upgrade-via-checkout and downgrade-via-change-plan
 * flows because the contract is shared.
 */
export function PlanChangeBlockedModal({
  open,
  onOpenChange,
  targetPlan,
  preflight,
  onResolveMembers,
  onResolveWorkspaceType,
  onResolveGuests,
  onResolveQuota,
  onResolveWorkspaces,
}: PlanChangeBlockedModalProps) {
  const targetName = targetPlan ? getPlanDisplayName(targetPlan) : 'this plan'

  const descriptorFor = (
    code: string,
    count?: number,
    currentValue?: string,
  ): ViolationDescriptor | null => {
    switch (code) {
      case PLAN_CHANGE_BLOCKED_CODES.MEMBERS: {
        const extra = count != null ? Math.max(0, count - 1) : null
        return {
          icon: UserMinus,
          title:
            extra != null
              ? `Remove ${extra} member${extra === 1 ? '' : 's'}`
              : 'Remove members',
          description: `${targetName} is solo only. Remove other members so the workspace has exactly one member.`,
          cta: 'Manage members',
          onResolve: onResolveMembers,
        }
      }
      case PLAN_CHANGE_BLOCKED_CODES.WORKSPACE_TYPE: {
        return {
          icon: Building2,
          title: 'Switch workspace type to Personal',
          description:
            currentValue && currentValue !== 'Personal'
              ? `${targetName} requires a Personal workspace. This workspace is currently set to ${currentValue}.`
              : `${targetName} requires a Personal workspace.`,
          cta: 'Open workspace settings',
          onResolve: onResolveWorkspaceType,
        }
      }
      case PLAN_CHANGE_BLOCKED_CODES.GUESTS: {
        const guestText = count != null ? `${count} guest${count === 1 ? '' : 's'}` : 'guests'
        return {
          icon: Users,
          title: `Remove ${guestText}`,
          description: `${targetName} doesn't allow guest access. Remove all guests before switching plans.`,
          cta: 'Manage members',
          onResolve: onResolveGuests,
        }
      }
      case PLAN_CHANGE_BLOCKED_CODES.QUOTA: {
        // Backend formats CurrentValue as "{key}:current/limit", e.g.
        // "tasks:147/100". Parse out the parts to render readable copy.
        const [limitKey, ratio] = (currentValue ?? '').split(':')
        const [currentStr, limitStr] = (ratio ?? '').split('/')
        const friendlyKey = friendlyQuotaKey(limitKey)
        const titleSuffix =
          currentStr && limitStr ? `(${currentStr} / ${limitStr})` : ''
        return {
          icon: BarChart3,
          title: `Reduce ${friendlyKey} ${titleSuffix}`.trim(),
          description: `${targetName} caps ${friendlyKey} below your current usage. Remove or archive items to drop below the cap.`,
          cta: `Manage ${friendlyKey}`,
          onResolve: onResolveQuota
            ? () => onResolveQuota(limitKey ?? '')
            : undefined,
        }
      }
      case PLAN_CHANGE_BLOCKED_CODES.WORKSPACES: {
        return {
          icon: Briefcase,
          title: 'You already own another Free workspace',
          description: `${targetName} allows only one workspace per user. Delete or transfer ownership of your other Free workspace before downgrading.`,
          cta: 'Open workspaces',
          onResolve: onResolveWorkspaces,
        }
      }
      default:
        // Unknown future codes fall through here. Render the raw code so QA
        // can spot a missing copy path.
        return {
          icon: AlertCircle,
          title: code,
          description: 'This change is blocked. Contact support if this is unexpected.',
        }
    }
  }

  const violations = preflight?.violations ?? []
  const isBlocked = preflight?.blocked === true && violations.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Switch to {targetName} blocked</DialogTitle>
          <DialogDescription>
            {isBlocked
              ? `Resolve the items below before switching to ${targetName}.`
              : `${targetName} can be selected once these checks pass.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {violations.map((violation, idx) => {
            const desc = descriptorFor(
              violation.code,
              violation.count,
              violation.currentValue,
            )
            if (!desc) return null
            const Icon = desc.icon
            return (
              <div
                key={`${violation.code}-${idx}`}
                className="flex items-start gap-3 rounded-md border bg-muted/30 p-3"
              >
                <Icon className="size-5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium leading-tight">{desc.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {desc.description}
                  </p>
                  {desc.cta && desc.onResolve && (
                    <Button
                      variant="link"
                      size="sm"
                      className="px-0 h-auto mt-1.5"
                      onClick={() => {
                        desc.onResolve!()
                        onOpenChange(false)
                      }}
                    >
                      {desc.cta} →
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
